import React from 'react';
import type { IconBaseProps, IconType } from 'react-icons';
import { FiCreditCard, FiStar, FiTruck } from 'react-icons/fi';

const renderIcon = (Icon: IconType, props?: IconBaseProps) =>
  React.createElement(Icon as unknown as React.FC<IconBaseProps>, props);

const messageBlocks = [
  {
    icon: FiTruck,
    title: 'Free Island-Wide Delivery',
    subtitle: 'Orders Above LKR 50,000',
    badge: 'Limited Time',
  },
  {
    icon: FiStar,
    title: 'Exclusive Tech Deals 2026',
    subtitle: 'Up to 40% OFF on Selected Items',
    badge: 'Hot Deal',
  },
  {
    icon: FiCreditCard,
    title: 'Easy Installment Plans Available',
    subtitle: 'Shop Now & Pay Later',
    badge: 'Flexi Pay',
  },
];

const DeliveryBanner: React.FC = () => {
  return (
    <div className="relative z-30 w-full overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
      {/* Animated shimmer effect */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s infinite linear',
          }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute left-[10%] top-1/2 h-2 w-2 rounded-full bg-white animate-float"
          style={{ animationDelay: '0s' }}
        />
        <div
          className="absolute left-[30%] top-1/2 h-1.5 w-1.5 rounded-full bg-white animate-float"
          style={{ animationDelay: '0.5s' }}
        />
        <div
          className="absolute right-[20%] top-1/2 h-2 w-2 rounded-full bg-white animate-float"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute right-[40%] top-1/2 h-1 w-1 rounded-full bg-white animate-float"
          style={{ animationDelay: '1.5s' }}
        />
      </div>

      <div className="relative flex w-full items-center overflow-hidden py-2 sm:py-2.5">
        <div className="delivery-marquee-track flex w-max flex-none items-center whitespace-nowrap text-white motion-reduce:animate-none">
          {[false, true].map((duplicate) => (
            <div
              key={duplicate ? 'duplicate' : 'primary'}
              className="flex flex-none items-center"
              aria-hidden={duplicate || undefined}
            >
              {messageBlocks.map((block) => (
                <div
                  key={block.title}
                  className="flex flex-none items-center gap-1.5 px-5 min-[375px]:gap-2 min-[375px]:px-7 sm:gap-3 sm:px-10 lg:px-14"
                >
                  <div className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-white/20 backdrop-blur-sm sm:h-8 sm:w-8 sm:rounded-lg">
                    {renderIcon(block.icon, { size: 15, className: 'sm:h-[18px] sm:w-[18px]' })}
                  </div>

                  <p className="flex-none text-[10px] font-bold tracking-wide min-[375px]:text-xs sm:text-sm">
                    {block.title}
                  </p>

                  <span className="flex-none text-[9px] font-semibold min-[375px]:text-[10px] sm:text-xs">
                    {block.subtitle}
                  </span>

                  <span className="inline-flex flex-none rounded-full border border-white/30 bg-slate-950/80 px-2 py-1 text-[8px] font-extrabold uppercase tracking-wide min-[375px]:text-[9px] sm:px-3 sm:text-[10px]">
                    {block.badge}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(-10px); opacity: 0.3; }
          50% { transform: translateY(10px); opacity: 0.8; }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite linear;
        }
        .animate-float {
          animation: float 3s infinite ease-in-out;
        }
        @keyframes delivery-marquee-scroll {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-50%, 0, 0);
          }
        }
        .delivery-marquee-track {
          animation: delivery-marquee-scroll 28s linear infinite;
          backface-visibility: hidden;
          transform: translate3d(0, 0, 0);
          will-change: transform;
        }
      `}</style>
    </div>
  );
};

export default DeliveryBanner;
